// UpsellModal — shown when a non-member tries to perform a gated action
// (editor mutation, AI feature). Routes to /pricing or to SignInPanel.

import SignInPanel from './SignInPanel';
import { useAuth } from './AuthProvider';

export type UpsellReason = 'edit' | 'ai' | 'tokens';

interface Props {
  open: boolean;
  reason: UpsellReason;
  onClose: () => void;
  onSeeMembership?: () => void;
  onSeeTokens?: () => void;
}

const COPY: Record<UpsellReason, { title: string; body: string }> = {
  edit: {
    title: 'Editing requires a membership',
    body:  'Membership unlocks the panel editor, layout tools, and AI features. Without one, ConstructiVision Web stays in read-only view.',
  },
  ai: {
    title: 'AI features require a membership',
    body:  'Pick simulation and auto-layout are part of the membership tier. They run on our servers and aren\'t covered by token purchases.',
  },
  tokens: {
    title: 'Out of tokens',
    body:  'Each PDF, DXF, CSV, or print costs one token. Top up with a 10-pack or a 100-pack — tokens never expire.',
  },
};

export default function UpsellModal({ open, reason, onClose, onSeeMembership, onSeeTokens }: Props) {
  const { session } = useAuth();
  if (!open) return null;
  const c = COPY[reason];

  return (
    <div className="cv-modal-backdrop" onClick={onClose}>
      <div className="cv-modal cv-upsell" onClick={e => e.stopPropagation()}>
        <button className="cv-modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2>{c.title}</h2>
        <p>{c.body}</p>

        {!session ? (
          <SignInPanel
            title="Start a free 7-day trial"
            subtitle="Trial includes editor + AI + 10 free tokens."
          />
        ) : (
          <div className="cv-upsell-actions">
            {reason !== 'tokens' && (
              <button className="cv-btn cv-btn-primary" onClick={onSeeMembership}>
                See membership options
              </button>
            )}
            {reason !== 'ai' && (
              <button className="cv-btn" onClick={onSeeTokens}>
                Buy tokens
              </button>
            )}
            <a className="cv-link" href={`${import.meta.env.BASE_URL}pricing`} target="_blank" rel="noreferrer">
              View pricing page
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
